import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ChevronUp, Droplets, Wind, Sun, CloudRain, Star, Shield, Sparkles, Timer, Truck, RotateCcw, Leaf, Check } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMAGES = {
  heroMan: 'https://static.prod-images.emergentagent.com/jobs/5d269af6-e239-4577-baf0-94dde8e2fa47/images/e28eef049f142ffc95e44f5b623fe3c6052e1ec8bab550c68d8184f20848819f.png',
  hardWater: 'https://images.unsplash.com/photo-1710395063369-cbdad5e70610?auto=format&fit=crop&w=600&q=80',
  humidity: 'https://images.unsplash.com/photo-1595776042969-89d50e7e47f6?auto=format&fit=crop&w=600&q=80',
  pollution: 'https://images.unsplash.com/photo-1596181576841-9bf4d437dd9e?auto=format&fit=crop&w=600&q=80',
  sun: 'https://images.unsplash.com/photo-1725119510161-10b53b29670714?auto=format&fit=crop&w=600&q=80',
  dailyWash: 'https://static.prod-images.emergentagent.com/jobs/5d269af6-e239-4577-baf0-94dde8e2fa47/images/e891ab9ff3247b938261353d229048e69844615a4365660cffad83d87921d560.png',
  dailyMoist: 'https://static.prod-images.emergentagent.com/jobs/5d269af6-e239-4577-baf0-94dde8e2fa47/images/2e3018c5938324df73faf317b86fd8c411aae189ce4202519f751b4b08290667.png',
  proDefence: 'https://static.prod-images.emergentagent.com/jobs/5d269af6-e239-4577-baf0-94dde8e2fa47/images/450acc76584999e808e9c7e4128afaac2cd6f736a02054f8daa8e03649e8d91f.png',
};

const TESTIMONIALS = [
  { quote: "Finally a moisturiser that doesn't turn greasy by 11am in Bangalore. SPF actually works — no white cast.", name: 'Rohan S.', city: 'Bangalore', rating: 5 },
  { quote: "The face wash doesn't leave my skin feeling sandpapered. Real difference in texture after two weeks.", name: 'Arjun M.', city: 'Mumbai', rating: 5 },
  { quote: "Hard water was destroying my skin. Two weeks with the daily wash and my dryness is gone.", name: 'Karan T.', city: 'Hyderabad', rating: 5 },
  { quote: "Non-greasy SPF that actually works in Chennai humidity. Game changer for outdoor commutes.", name: 'Aditya R.', city: 'Chennai', rating: 5 },
  { quote: "My post-shave bumps have reduced significantly in four weeks. Genuinely impressed.", name: 'Vikram P.', city: 'Delhi', rating: 4 },
  { quote: "Simple routine, visible results. My skin looks cleaner and more even after 3 weeks.", name: 'Siddharth N.', city: 'Pune', rating: 4 },
];

const GOOD_FOR = {
  'daily-face-wash': ['Oily skin', 'Daily use'],
  'pro-active-face-wash': ['Acne-prone', 'Deep clean'],
  'daily-moisturiser-spf30': ['All skin types', 'Daily SPF'],
  'pro-defence-moisturiser-spf50': ['Sun exposure', 'High UV days'],
};

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function HomePage() {
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [showTop, setShowTop] = useState(false);
  const location = useLocation();

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(err => console.error('API request failed:', err));
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [location.hash]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const skincare = products.filter(p => p.category === 'face').sort((a, b) => a.tier === 'daily' ? -1 : 1);
  const starterDuo = [
    products.find(p => p.slug === 'daily-face-wash'),
    products.find(p => p.slug === 'daily-moisturiser-spf30'),
  ].filter(Boolean);

  return (
    <div className="bg-white">
      {/* 1. HERO */}
      <HeroSection />
      {/* 2. TRUST BAR — immediately below hero */}
      <TrustBar />
      {/* 3. REVIEWS — top third of the page */}
      <TestimonialsSection />
      {/* 4. PROBLEM */}
      <ProblemSection />
      {/* 5. SOLUTION */}
      <SolutionSection />
      {/* 6. AS SEEN ON */}
      <AsSeenOn />
      {/* 7. YOU ONLY NEED 2 PRODUCTS */}
      <TwoProductsSection duo={starterDuo} addItem={addItem} />
      {/* 8. THE RANGE — 4 skincare products */}
      <BestsellersSection products={skincare} addItem={addItem} />
      {/* 9. ROUTINE */}
      <RoutineSection />
      {/* 10. RESULTS */}
      <ResultsSection />
      {/* 11. FOUNDER */}
      <FounderSection />
      {/* 12. SCIENCE CTA */}
      <ScienceCTA />
      {/* 13. HAIR WAITLIST */}
      <HairWaitlistSection />
      {/* 14. TRUST BENEFITS STRIP */}
      <TrustBenefitsStrip />
      {/* 15. FINAL CTA */}
      <FinalCTA />

      <button
        data-testid="back-to-top-btn"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full bg-[#0C4A8C] text-white flex items-center justify-center shadow-lg transition-all duration-300 hover:bg-[#093B70] ${showTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
        aria-label="Back to top"
      >
        <ChevronUp size={18} strokeWidth={2} />
      </button>
    </div>
  );
}

/* ──────────────────── 1. HERO ──────────────────── */
function HeroSection() {
  return (
    <section id="hero" data-testid="hero-section" className="bg-[#0C4A8C] relative overflow-hidden grain-overlay">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[85vh]">
          <div className="flex flex-col justify-center px-8 md:px-16 lg:px-20 py-20 lg:py-0 opacity-0 anim-left" style={{ animationDelay: '0.15s', animationFillMode: 'forwards' }}>
            <p className="eyebrow mb-6 text-[#BAE6FD]">Built for Indian conditions</p>
            <h1 data-testid="hero-headline" className="font-serif font-semibold text-4xl sm:text-5xl lg:text-[62px] text-white leading-[1.08] mb-6 tracking-tight">
              Skincare that survives<br />
              <span className="italic" style={{ color: '#BAE6FD' }}>Indian water, sun & sweat.</span>
            </h1>
            <p data-testid="hero-subtext" className="text-base lg:text-lg font-light text-white/60 leading-relaxed mb-10 max-w-md">
              Hard water at 300ppm. UV index 8+. Monsoon humidity.<br />
              Four products, formulated for the skin you actually live in.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <Link to="/shop" data-testid="hero-cta-routine"
                className="bg-white text-[#0C4A8C] py-3.5 px-8 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[3px] hover:bg-[#F0F9FF] transition-colors text-center">
                Shop the Range
              </Link>
              <Link to="/science" data-testid="hero-cta-bestsellers"
                className="border rounded-[2px] py-3.5 px-8 text-[11px] font-sans font-medium uppercase tracking-[3px] hover:bg-white/5 transition-colors text-center"
                style={{ borderColor: 'rgba(186,230,253,0.35)', color: 'rgba(255,255,255,0.7)' }}>
                See the Science
              </Link>
            </div>

            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-1.5">
                {[1,2,3,4,5].map(s => <Star key={s} size={13} className="text-[#BAE6FD] fill-[#BAE6FD]" />)}
                <span className="text-[11px] text-white/50 ml-1 font-sans">4.8/5</span>
              </div>
              <span className="text-[11px] text-white/35 font-sans">2,500+ customers across India</span>
            </div>
          </div>

          <div className="relative opacity-0 anim-right hidden lg:block" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
            <img src={IMAGES.heroMan} alt="Indian man with clear skin"
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(0.85) contrast(1.05)' }} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #0C4A8C 0%, transparent 30%)' }} />

            <div className="absolute bottom-12 left-8 bg-[#062B52]/90 backdrop-blur-md rounded-[2px] p-5" style={{ border: '1px solid rgba(186,230,253,0.25)' }}>
              <p className="text-[9px] font-sans uppercase tracking-[2px] text-[#BAE6FD] font-medium mb-1">pH Balanced</p>
              <p className="text-[30px] font-serif text-white font-bold leading-none">4.8–5.5</p>
              <p className="text-[10px] font-sans text-white/45 mt-1">Every single product</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────── 2. TRUST BAR ──────────────────── */
function TrustBar() {
  return (
    <section data-testid="trust-bar" className="bg-white py-5" style={{ borderBottom: '1px solid rgba(12,74,140,0.08)' }}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-6 md:gap-12 flex-wrap">
        <div className="flex items-center gap-2" data-testid="trust-bar-rating">
          <div className="flex items-center gap-0.5">
            {[1,2,3,4,5].map(s => <Star key={s} size={14} className="text-[#0C7BC0] fill-[#0C7BC0]" />)}
          </div>
          <span className="text-[13px] font-sans font-semibold text-[#0C4A8C]">4.8/5</span>
          <span className="text-[11px] font-sans text-[#6B7D8A]">· 2,500+ reviews</span>
        </div>
        <span className="hidden md:block w-px h-5" style={{ background: 'rgba(12,74,140,0.12)' }} />
        {[
          { icon: Droplets, text: 'Made for Indian conditions' },
          { icon: Sun, text: 'No white cast' },
          { icon: Leaf, text: 'Sulphate-free' },
          { icon: Shield, text: 'Dermatologically guided' },
        ].map((t, i) => (
          <div key={t.text} className="flex items-center gap-2" data-testid={`trust-marker-${i}`}>
            <t.icon size={13} className="text-[#0C7BC0]" />
            <span className="text-[10px] font-sans tracking-[1.5px] uppercase text-[#0C4A8C]/60 font-medium">{t.text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────── 3. TESTIMONIALS ──────────────────── */
function TestimonialsSection() {
  const [ref, visible] = useInView();
  return (
    <section id="testimonials" ref={ref} data-testid="testimonials-section" className="bg-[#F0F9FF] py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-6xl mx-auto">
        <div className={`flex flex-col md:flex-row md:items-end md:justify-between mb-14 ${visible ? 'opacity-100' : 'opacity-0'} transition-all duration-700`}>
          <div>
            <p className="eyebrow text-[#0C7BC0] mb-4">Don't take our word for it</p>
            <h2 className="text-3xl md:text-4xl lg:text-[44px] font-serif font-semibold text-[#0C4A8C] tracking-tight">Real men. Real cities.<br className="hidden md:block" /> Real results.</h2>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-4">
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(s => <Star key={s} size={15} className="text-[#0C7BC0] fill-[#0C7BC0]" />)}
            </div>
            <div>
              <p className="text-[15px] font-sans text-[#0C4A8C] font-semibold">4.8 / 5</p>
              <p className="text-[10px] font-sans text-[#6B7D8A]">2,500+ reviews</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TESTIMONIALS.slice(0, 6).map((t, i) => (
            <div key={t.name} data-testid={`testimonial-${i}`}
              className={`p-6 rounded-[3px] bg-white ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} transition-all duration-700`}
              style={{ transitionDelay: `${i * 100}ms`, borderLeft: '2px solid #0C4A8C', boxShadow: '0 1px 3px rgba(12,74,140,0.06)' }}>
              <div className="flex items-center gap-0.5 mb-4">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={11} className={s <= (t.rating || 5) ? 'text-[#0C7BC0] fill-[#0C7BC0]' : 'text-[#CBD5E1]'} />
                ))}
              </div>
              <p className="text-[15px] text-[#3D4F5C] font-serif italic leading-relaxed mb-5">"{t.quote}"</p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-[4px] bg-[#0C4A8C] text-white flex items-center justify-center text-[13px] font-serif font-bold leading-none">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <p className="text-[12px] text-[#0C4A8C] font-sans font-medium">{t.name}</p>
                  <p className="text-[10px] text-[#6B7D8A] font-sans">{t.city} · Verified buyer</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────── 4. PROBLEM ──────────────────── */
function ProblemSection() {
  const [ref, visible] = useInView();
  const problems = [
    { icon: Droplets, img: IMAGES.hardWater, title: 'Hard Water', effect: 'Dryness & irritation', stat: '300+ ppm', statLabel: 'in most Indian cities', desc: 'Strips your natural moisture barrier every single wash.' },
    { icon: CloudRain, img: IMAGES.humidity, title: 'Humidity', effect: 'Clogged pores & breakouts', stat: '70–90%', statLabel: 'humidity year-round', desc: 'Most moisturisers turn greasy and clog your pores.' },
    { icon: Wind, img: IMAGES.pollution, title: 'Pollution', effect: 'Dullness & aging', stat: 'AQI 150+', statLabel: 'in major metros', desc: 'PM2.5 particles penetrate skin and accelerate damage.' },
    { icon: Sun, img: IMAGES.sun, title: 'UV Exposure', effect: 'Tanning & dark spots', stat: 'UV 8–12', statLabel: 'daily index', desc: 'Most SPFs tested for UV 4–6. Not built for Indian sun.' },
  ];

  return (
    <section id="problem" ref={ref} data-testid="problem-section" className="py-24 md:py-32 px-6 md:px-12 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className={`text-center mb-16 ${visible ? 'opacity-100' : 'opacity-0'} transition-all duration-700`}>
          <p className="eyebrow text-[#0C7BC0] mb-4">The Problem</p>
          <h2 className="text-3xl md:text-4xl lg:text-[44px] font-serif font-semibold text-[#0C4A8C] tracking-tight mb-4 leading-tight">Your environment attacks your skin<br className="hidden md:block" /> every single day.</h2>
          <p className="text-sm text-[#6B7D8A] font-light max-w-lg mx-auto">Most skincare is formulated for Western conditions. Your skin faces a completely different reality.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {problems.map((card, i) => (
            <div key={card.title} data-testid={`problem-card-${i}`}
              className={`rounded-[3px] overflow-hidden group bg-white ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} transition-all duration-700`}
              style={{ transitionDelay: `${i * 120}ms`, boxShadow: '0 1px 3px rgba(12,74,140,0.08)', border: '1px solid rgba(12,74,140,0.06)' }}>
              <div className="h-36 overflow-hidden relative">
                <img src={card.img} alt={card.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #062B52 0%, rgba(6,43,82,0.3) 100%)' }} />
                <div className="absolute bottom-3 left-4 flex items-center gap-2">
                  <card.icon size={15} className="text-[#BAE6FD]" />
                  <p className="text-[15px] font-serif text-white font-semibold">{card.title}</p>
                </div>
              </div>
              <div className="p-5">
                <p className="text-[10px] font-sans font-semibold text-[#0C7BC0] uppercase tracking-[1.5px] mb-2">{card.effect}</p>
                <div className="flex items-baseline gap-1.5 mb-2">
                  <span className="font-serif text-[24px] text-[#0C4A8C] font-bold leading-none">{card.stat}</span>
                  <span className="text-[10px] font-sans text-[#6B7D8A]">{card.statLabel}</span>
                </div>
                <p className="text-[12px] text-[#6B7D8A] font-light leading-relaxed">{card.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────── 5. SOLUTION ──────────────────── */
function SolutionSection() {
  const [ref, visible] = useInView();
  return (
    <section id="solution" ref={ref} data-testid="solution-section" className="bg-[#0C4A8C] relative grain-overlay py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-6xl mx-auto relative z-10">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mb-16 ${visible ? 'opacity-100' : 'opacity-0'} transition-all duration-700`}>
          <div className="lg:col-span-4">
            <p className="eyebrow mb-4 text-[#BAE6FD]">The Solution</p>
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-white tracking-tight mb-4 leading-tight">Engineered for Indian conditions.</h2>
            <p className="text-sm font-light text-white/50 mb-6">Every formula tested at 300ppm water hardness, UV index 8–11, and 80% humidity — on Indian skin tones.</p>
            <Link to="/science" className="inline-flex items-center gap-2 text-[10px] font-sans font-medium uppercase tracking-[2.5px] text-[#BAE6FD] hover:text-white transition-colors">
              Explore Ingredients <ArrowRight size={12} />
            </Link>
          </div>

          <div className="lg:col-span-4 flex items-center justify-center gap-3">
            {[
              { img: IMAGES.dailyWash, label: 'Daily Face Wash', size: 'w-24 h-36' },
              { img: IMAGES.dailyMoist, label: 'Daily Moisturiser SPF 30', size: 'w-28 h-40' },
              { img: IMAGES.proDefence, label: 'Pro Defence SPF 50', size: 'w-24 h-36' },
            ].map((b) => (
              <div key={b.label} className={`${b.size} bg-white/5 rounded-[3px] overflow-hidden`} style={{ border: '1px solid rgba(186,230,253,0.15)' }}>
                <img src={b.img} alt={b.label} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>

          <div className="lg:col-span-4 space-y-5">
            {[
              { icon: Shield, title: 'Hard Water Safe', desc: 'Protects against dryness and irritation.' },
              { icon: Sparkles, title: 'Lightweight & Non-Greasy', desc: 'Absorbs fast. Incredibly light.' },
              { icon: Sun, title: 'No White Cast Sunscreen', desc: 'Blends invisible, leaves no residue.' },
              { icon: Wind, title: 'Tough on Pollutants', desc: 'Defends, so your skin stays healthy.' },
            ].map((b, i) => (
              <div key={b.title} data-testid={`solution-benefit-${i}`} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(186,230,253,0.12)' }}>
                  <b.icon size={16} className="text-[#BAE6FD]" />
                </div>
                <div>
                  <p className="text-[13px] text-white font-medium mb-0.5">{b.title}</p>
                  <p className="text-[11px] text-white/45 font-light">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-5">
          {[
            { val: 'Zero', label: 'Sulphates', sub: 'Gentle, barrier-safe cleansing' },
            { val: '2–5%', label: 'Niacinamide', sub: 'Active in every product' },
            { val: '100%', label: 'Transparent', sub: 'Every ingredient explained' },
          ].map((s) => (
            <div key={s.label} className="text-center p-5 rounded-[3px]" style={{ background: 'rgba(186,230,253,0.06)' }}>
              <p className="font-serif text-[32px] text-white font-bold leading-none">{s.val}</p>
              <p className="text-[10px] text-[#BAE6FD] font-sans font-medium mt-1 uppercase tracking-[1px]">{s.label}</p>
              <p className="text-[9px] text-white/35 font-sans mt-1">{s.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────── 6. AS SEEN ON ──────────────────── */
function AsSeenOn() {
  return (
    <section data-testid="as-seen-on" className="py-14 px-6 md:px-12 bg-white" style={{ borderBottom: '1px solid rgba(12,74,140,0.06)' }}>
      <div className="max-w-5xl mx-auto">
        <p className="text-center text-[10px] font-sans font-medium uppercase tracking-[3px] text-[#6B7D8A] mb-8">As Seen On</p>
        <div className="flex items-center justify-center gap-10 md:gap-16 flex-wrap opacity-40">
          {['GQ', 'MENSXP', 'INDIA TODAY', 'BUSINESS INSIDER', 'YOURSTORY', 'THE HINDU'].map((pub, i) => (
            <span key={pub} data-testid={`press-logo-${i}`} className="text-[15px] md:text-[17px] font-serif font-bold text-[#0C4A8C] tracking-[1px] uppercase whitespace-nowrap">{pub}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────── 7. YOU ONLY NEED 2 PRODUCTS ──────────────────── */
function TwoProductsSection({ duo, addItem }) {
  const [ref, visible] = useInView();
  const total = duo.reduce((s, p) => s + p.price, 0);
  const addBoth = () => duo.forEach(p => addItem({ id: p.slug, name: p.name, price: p.price, size: p.size, image: p.image || '', quantity: 1 }));

  return (
    <section id="starter" ref={ref} data-testid="two-products-section" className="bg-[#F0F9FF] py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-5xl mx-auto">
        <div className={`text-center mb-14 ${visible ? 'opacity-100' : 'opacity-0'} transition-all duration-700`}>
          <p className="eyebrow text-[#0C7BC0] mb-4">Keep it simple</p>
          <h2 className="text-3xl md:text-4xl lg:text-[44px] font-serif font-semibold text-[#0C4A8C] tracking-tight mb-4 leading-tight">You only need 2 products to start.</h2>
          <p className="text-sm text-[#6B7D8A] font-light max-w-lg mx-auto">The #1 reason men quit skincare? Complicated routines. Wash. Protect. Done in 60 seconds — morning and night.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {duo.map((p, i) => (
            <div key={p.slug} data-testid={`starter-product-${p.slug}`}
              className={`bg-white rounded-[3px] overflow-hidden flex items-center ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} transition-all duration-700`}
              style={{ transitionDelay: `${i * 150}ms`, boxShadow: '0 1px 3px rgba(12,74,140,0.08)', border: '1px solid rgba(12,74,140,0.06)' }}>
              <Link to={`/product/${p.slug}`} className="w-32 h-36 flex-shrink-0 bg-[#F0F9FF] overflow-hidden">
                {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}
              </Link>
              <div className="p-5 flex-1">
                <p className="text-[9px] font-sans uppercase tracking-[2px] font-semibold text-[#0C7BC0] mb-1">Step {i + 1} · {i === 0 ? 'Wash' : 'Protect'}</p>
                <h3 className="font-serif text-[20px] font-semibold leading-tight text-[#0C4A8C]">{p.name.replace('TRUFORM ', '')}</h3>
                <p className="text-[12px] font-sans text-[#6B7D8A] mt-0.5">&#8377;{p.price} / {p.size}</p>
                {p.ph && <span className="inline-block mt-2 text-[9px] font-sans font-semibold text-[#0C4A8C] bg-[#BAE6FD]/50 px-2 py-0.5 rounded-[2px]">pH {p.ph}</span>}
              </div>
            </div>
          ))}
        </div>

        {duo.length === 2 && (
          <div className="text-center">
            <button data-testid="starter-add-both-btn" onClick={addBoth}
              className="inline-flex items-center gap-2 bg-[#0C4A8C] text-white px-10 py-4 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[3px] hover:bg-[#093B70] transition-colors">
              Start with Both — &#8377;{total} <ArrowRight size={14} />
            </button>
            <p className="text-[11px] text-[#6B7D8A] mt-4 font-sans">Free shipping above &#8377;499 · 7-day returns if it's not for you</p>
          </div>
        )}
      </div>
    </section>
  );
}

/* ──────────────────── 8. THE RANGE ──────────────────── */
function BestsellersSection({ products, addItem }) {
  const [ref, visible] = useInView();
  return (
    <section id="products" ref={ref} data-testid="bestsellers-section" className="py-24 md:py-32 px-6 md:px-12 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className={`flex flex-col md:flex-row md:items-end md:justify-between mb-14 ${visible ? 'opacity-100' : 'opacity-0'} transition-all duration-700`}>
          <div>
            <p className="eyebrow text-[#0C7BC0] mb-4">The Range</p>
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#0C4A8C] tracking-tight">Skin first. Four products. Zero fluff.</h2>
          </div>
          <Link to="/shop" data-testid="view-all-products"
            className="mt-4 md:mt-0 inline-flex items-center gap-2 text-[10px] font-sans font-medium uppercase tracking-[2.5px] text-[#0C4A8C]/60 hover:text-[#0C4A8C] transition-colors">
            View all <ArrowRight size={12} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.slice(0, 4).map((p, i) => (
            <ProductCard key={p.slug} product={p} onAdd={addItem} delay={i * 100} visible={visible} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────── 9. ROUTINE ──────────────────── */
function RoutineSection() {
  const [ref, visible] = useInView();
  const steps = [
    { num: '01', icon: Droplets, title: 'Clean', time: '30 sec', desc: 'pH-balanced, sulphate-free face wash removes dirt without stripping your barrier. Works with hard water.', products: 'Daily or Pro Active Face Wash' },
    { num: '02', icon: Shield, title: 'Protect', time: '30 sec', desc: 'Lightweight moisturiser with built-in SPF 30–50. No white cast, non-greasy even in 80% humidity.', products: 'Daily Moisturiser SPF 30 or Pro Defence SPF 50' },
  ];

  return (
    <section id="routines" ref={ref} data-testid="routine-section" className="bg-[#F0F9FF] py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        <div className={`text-center mb-16 ${visible ? 'opacity-100' : 'opacity-0'} transition-all duration-700`}>
          <p className="eyebrow text-[#0C7BC0] mb-4">60-Second Routine</p>
          <h2 className="text-3xl md:text-4xl lg:text-[44px] font-serif font-semibold text-[#0C4A8C] tracking-tight mb-4 leading-tight">Simple enough to stick with.</h2>
          <p className="text-sm text-[#6B7D8A] font-light max-w-md mx-auto">Healthy skin doesn't need 7 steps. It needs 2 — done consistently.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
          {steps.map((step, i) => (
            <div key={step.num} data-testid={`routine-step-${i}`}
              className={`text-center p-8 rounded-[3px] relative bg-white ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} transition-all duration-700`}
              style={{ transitionDelay: `${i * 150}ms`, border: '1px solid rgba(12,74,140,0.06)', boxShadow: '0 1px 3px rgba(12,74,140,0.06)' }}>
              <div className="absolute top-4 left-5">
                <span className="font-serif text-[52px] font-bold leading-none" style={{ color: 'rgba(12,74,140,0.06)' }}>{step.num}</span>
              </div>
              <div className="w-14 h-14 rounded-full bg-[#0C4A8C] text-[#BAE6FD] flex items-center justify-center mx-auto mb-6">
                <step.icon size={22} />
              </div>
              <h3 className="font-serif text-2xl text-[#0C4A8C] font-semibold mb-2">{step.title}</h3>
              <p className="text-[11px] font-sans text-[#0C7BC0] font-medium uppercase tracking-[2px] mb-4 flex items-center justify-center gap-1.5">
                <Timer size={12} /> {step.time}
              </p>
              <p className="text-[13px] text-[#6B7D8A] font-light leading-relaxed mb-4">{step.desc}</p>
              <p className="text-[10px] font-sans text-[#0C4A8C]/50 uppercase tracking-[1px]">{step.products}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link to="/shop" data-testid="routine-shop-btn"
            className="inline-flex items-center gap-2 bg-[#0C4A8C] text-white px-8 py-3.5 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[3px] hover:bg-[#093B70] transition-colors">
            Start Your Routine <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────── 10. RESULTS ──────────────────── */
function ResultsSection() {
  const [ref, visible] = useInView();
  const results = [
    { week: 'Week 1', title: 'Reduced dryness', desc: 'Hard water damage starts reversing. Skin feels less tight after washing.', pct: '87%', pctLabel: 'noticed softer skin' },
    { week: 'Week 2–3', title: 'Clearer texture', desc: 'Pores unclog. Oiliness reduces. Post-shave bumps start fading.', pct: '73%', pctLabel: 'saw reduced breakouts' },
    { week: 'Week 4+', title: 'Even tone', desc: 'Dark spots lighten. Skin looks healthier, more even. SPF prevents new damage.', pct: '91%', pctLabel: 'recommend to friends' },
  ];

  return (
    <section id="results" ref={ref} data-testid="results-section" className="py-24 md:py-32 px-6 md:px-12 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className={`text-center mb-16 ${visible ? 'opacity-100' : 'opacity-0'} transition-all duration-700`}>
          <p className="eyebrow text-[#0C7BC0] mb-4">Results</p>
          <h2 className="text-3xl md:text-4xl lg:text-[44px] font-serif font-semibold text-[#0C4A8C] tracking-tight mb-4 leading-tight">Visible change. Real timelines.</h2>
          <p className="text-sm text-[#6B7D8A] font-light max-w-lg mx-auto">What to expect when you switch to skincare built for your conditions.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {results.map((r, i) => (
            <div key={r.week} data-testid={`result-card-${i}`}
              className={`p-7 rounded-[3px] bg-[#F0F9FF] relative ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} transition-all duration-700`}
              style={{ transitionDelay: `${i * 150}ms`, border: '1px solid rgba(12,74,140,0.06)' }}>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-px bg-[#0C7BC0]" />
                <p className="text-[10px] font-sans font-medium text-[#0C7BC0] uppercase tracking-[2px]">{r.week}</p>
              </div>
              <h3 className="font-serif text-xl text-[#0C4A8C] font-semibold mb-3">{r.title}</h3>
              <p className="text-[13px] text-[#6B7D8A] font-light leading-relaxed mb-5">{r.desc}</p>
              <div className="flex items-end gap-2 pt-4" style={{ borderTop: '1px solid rgba(12,74,140,0.08)' }}>
                <span className="font-serif text-[38px] text-[#0C4A8C] font-bold leading-none">{r.pct}</span>
                <span className="text-[11px] text-[#6B7D8A] font-sans pb-1.5">{r.pctLabel}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────── 11. FOUNDER ──────────────────── */
function FounderSection() {
  const [ref, visible] = useInView();
  return (
    <section id="founder" ref={ref} data-testid="founder-section" className="bg-[#0C4A8C] relative grain-overlay py-24 md:py-32 px-6 md:px-12">
      <div className={`max-w-2xl mx-auto text-center relative z-10 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} transition-all duration-700`}>
        <p className="eyebrow mb-5 text-[#BAE6FD]">The Founder</p>
        <h2 className="text-3xl md:text-4xl font-serif font-semibold text-white tracking-tight mb-8">Why I built TRUFORM.</h2>
        <div className="space-y-5 mb-10">
          <p className="text-[16px] font-light leading-relaxed text-white/60">
            "I spent years using skincare brands that were made for conditions nothing like India. My face was dry from hard water. Moisturisers turned greasy by noon in Mumbai humidity. Sunscreens left white streaks on my skin tone."
          </p>
          <p className="text-[16px] font-light leading-relaxed text-white/60">
            "So I built what I wanted to use — products formulated specifically for Indian water hardness, humidity, UV levels, and skin tones. No compromises. Every ingredient has a reason."
          </p>
        </div>
        <div className="flex items-center justify-center gap-3">
          <div className="w-10 h-10 rounded-[4px] bg-white flex items-center justify-center font-serif text-[20px] font-bold leading-none"><span style={{ color: '#0C4A8C' }}>T</span></div>
          <div className="text-left">
            <p className="text-[13px] text-white font-sans font-medium">Founder, TRUFORM MEN</p>
            <p className="text-[11px] text-white/40 font-sans">Mumbai, India</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────── 12. SCIENCE CTA ──────────────────── */
function ScienceCTA() {
  const [ref, visible] = useInView();
  return (
    <section id="science" ref={ref} data-testid="science-cta" className="bg-white py-24 md:py-32 px-6 md:px-12">
      <div className={`max-w-3xl mx-auto text-center ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} transition-all duration-700`}>
        <p className="eyebrow mb-4 text-[#0C7BC0]">Ingredient Transparency</p>
        <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#0C4A8C] tracking-tight mb-4">We explain everything.</h2>
        <p className="text-sm font-light mb-10 max-w-md mx-auto text-[#6B7D8A]">Every ingredient. Every concentration. Every reason it's there.</p>
        <Link to="/science" data-testid="science-cta-btn"
          className="inline-flex items-center gap-2 bg-[#0C4A8C] text-white px-8 py-3.5 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[3px] hover:bg-[#093B70] transition-colors">
          Read The Science <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}

/* ──────────────────── 13. HAIR WAITLIST ──────────────────── */
function HairWaitlistSection() {
  const [ref, visible] = useInView();
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, { email, source: 'hair-waitlist' });
      setDone(true);
      setEmail('');
    } catch (err) { console.error('Waitlist signup failed:', err); } finally { setSubmitting(false); }
  };

  return (
    <section id="hair-waitlist" ref={ref} data-testid="hair-waitlist-section" className="bg-[#062B52] relative grain-overlay py-20 md:py-28 px-6 md:px-12">
      <div className={`max-w-2xl mx-auto text-center relative z-10 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} transition-all duration-700`}>
        <p className="eyebrow mb-4 text-[#BAE6FD]">Coming Soon</p>
        <h2 className="text-3xl md:text-4xl font-serif font-semibold text-white tracking-tight mb-4">Hair care, built the same way.</h2>
        <p className="text-sm font-light text-white/50 mb-8 max-w-md mx-auto">pH-corrected, sulphate-free scalp care formulated for Indian hard water — launching soon. Be first to know.</p>
        {done ? (
          <p data-testid="hair-waitlist-success" className="text-[14px] text-[#BAE6FD] font-sans">You're on the list. We'll email you at launch.</p>
        ) : (
          <form onSubmit={submit} data-testid="hair-waitlist-form" className="flex max-w-md mx-auto">
            <input
              data-testid="hair-waitlist-email"
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Enter your email" required
              className="flex-1 bg-white/5 border border-white/15 text-white text-[13px] px-4 py-3.5 rounded-l-[2px] focus:outline-none focus:border-[#BAE6FD] placeholder-white/30 font-sans"
            />
            <button data-testid="hair-waitlist-submit" type="submit" disabled={submitting}
              className="bg-[#BAE6FD] text-[#062B52] px-6 py-3.5 rounded-r-[2px] text-[11px] font-sans font-semibold uppercase tracking-[2px] hover:bg-white transition-colors disabled:opacity-50">
              {submitting ? '...' : 'Notify Me'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

/* ──────────────────── 14. TRUST BENEFITS STRIP ──────────────────── */
function TrustBenefitsStrip() {
  const [ref, visible] = useInView();
  return (
    <section ref={ref} data-testid="trust-benefits-strip" className="py-16 px-6 md:px-12 bg-white" style={{ borderTop: '1px solid rgba(12,74,140,0.06)', borderBottom: '1px solid rgba(12,74,140,0.06)' }}>
      <div className={`max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 ${visible ? 'opacity-100' : 'opacity-0'} transition-all duration-700`}>
        {[
          { icon: Truck, title: 'Free Shipping', desc: 'On orders above ₹499' },
          { icon: RotateCcw, title: 'Easy Returns', desc: '7-day hassle-free returns' },
          { icon: Shield, title: 'Dermat Guided', desc: 'Safe for all skin types' },
          { icon: Leaf, title: 'Sulphate-Free', desc: 'No SLS, no parabens' },
        ].map((b) => (
          <div key={b.title} className="text-center">
            <div className="w-11 h-11 rounded-full bg-[#0C4A8C] text-[#BAE6FD] flex items-center justify-center mx-auto mb-3">
              <b.icon size={18} />
            </div>
            <p className="text-[15px] font-serif text-[#0C4A8C] font-semibold mb-1">{b.title}</p>
            <p className="text-[11px] text-[#6B7D8A] font-light">{b.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────── 15. FINAL CTA ──────────────────── */
function FinalCTA() {
  return (
    <section data-testid="final-cta" className="bg-[#0C4A8C] relative grain-overlay py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-2xl mx-auto text-center relative z-10">
        <h2 className="text-3xl md:text-4xl lg:text-[44px] font-serif font-semibold text-white tracking-tight mb-5 leading-tight">Ready to fix your skin?</h2>
        <p className="text-sm font-light mb-10 text-white/50">Join 2,500+ Indian men who switched to skincare that actually works for their conditions.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/shop" data-testid="final-cta-btn"
            className="bg-white text-[#0C4A8C] px-8 py-3.5 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[3px] hover:bg-[#F0F9FF] transition-colors text-center">
            Shop the Range
          </Link>
        </div>
        <p className="text-[11px] text-white/30 mt-5 font-sans">Free shipping above &#8377;499 &middot; Easy returns &middot; 3-5 day delivery</p>
      </div>
    </section>
  );
}

/* ──────────────────── PRODUCT CARD ──────────────────── */
function ProductCard({ product: p, onAdd, delay = 0, visible = true }) {
  const isPro = p.tier === 'pro';
  const goodFor = GOOD_FOR[p.slug] || [];
  const rating = isPro ? 4.9 : 4.7;

  return (
    <div data-testid={`product-card-${p.slug}`}
      className={`rounded-[3px] overflow-hidden bg-white group hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} transition-all duration-500`}
      style={{ transitionDelay: `${delay}ms`, boxShadow: '0 1px 3px rgba(12,74,140,0.08)', border: isPro ? '1px solid #0C7BC0' : '1px solid rgba(12,74,140,0.1)' }}>
      <Link to={`/product/${p.slug}`} className="block">
        {p.image && (
          <div className="h-44 overflow-hidden bg-[#F0F9FF] relative" data-testid={`product-image-${p.slug}`}>
            <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            {isPro && (
              <span className="absolute top-3 right-3 text-[7px] font-sans font-semibold uppercase tracking-[2px] px-2.5 py-1 rounded-[2px] bg-[#0C7BC0] text-white">PRO</span>
            )}
          </div>
        )}
        <div className="p-5 flex flex-col gap-1.5" style={{ borderLeft: isPro ? '3px solid #0C7BC0' : '3px solid #0C4A8C' }}>
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-sans uppercase tracking-[2px] font-semibold" style={{ color: '#0C7BC0' }}>
              {isPro ? 'Pro' : 'Daily'}
            </p>
            <div className="flex items-center gap-0.5">
              <Star size={10} className="text-[#0C7BC0] fill-[#0C7BC0]" />
              <span className="text-[10px] font-sans text-[#6B7D8A]">{rating}</span>
            </div>
          </div>
          <h4 className="font-serif text-[18px] font-semibold leading-tight text-[#0C4A8C]">
            {p.name.replace('TRUFORM ', '')}
          </h4>
          <span className="text-[12px] font-sans text-[#6B7D8A]">&#8377;{p.price} / {p.size}</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {p.ph && <span className="text-[8px] font-sans font-semibold text-[#0C4A8C] bg-[#BAE6FD]/50 px-1.5 py-0.5 rounded-[2px]">pH {p.ph}</span>}
            {goodFor.map((g) => (
              <span key={g} className="text-[8px] font-sans text-[#0C4A8C] bg-[#F0F9FF] px-1.5 py-0.5 rounded-[2px]">{g}</span>
            ))}
          </div>
        </div>
      </Link>
      <div className="px-5 py-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(12,74,140,0.06)' }}>
        <button data-testid={`add-to-cart-${p.slug}`}
          onClick={() => onAdd({ id: p.slug, name: p.name, price: p.price, size: p.size, image: p.image || '', quantity: 1 })}
          className="text-[9px] font-sans uppercase tracking-[2px] text-[#0C4A8C]/60 hover:text-[#0C4A8C] font-medium transition-colors">
          + Add to Cart
        </button>
      </div>
    </div>
  );
}
